import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/services/api";
import useStore from "@/services/store";

import Dilemmas from "./dilemmas";
import Signup from "./signup";

function slugify(str) {
  return (str || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function Spinner({ size }) {
  const cls = size === "lg" ? "h-8 w-8" : "h-4 w-4";
  return (
    <svg className={`animate-spin ${cls}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

export default function AuthRecruiterPage() {
  const [searchParams] = useSearchParams();
  const websiteParam = searchParams.get("website") || "";

  const [step, setStep] = useState("generating");

  // Dilemma data
  const [website, setWebsite] = useState(websiteParam);
  const [dilemmas, setDilemmas] = useState([]);
  const [companyContext, setCompanyContext] = useState("");
  const [currentDilemma, setCurrentDilemma] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [genError, setGenError] = useState("");

  // Results
  const [profile, setProfile] = useState(null);

  // Signup (pre-filled from scraping)
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const slug = slugify(companyName);

  useEffect(() => {
    if (websiteParam) {
      generateDilemmas(websiteParam);
    } else {
      setStep("enter-website");
    }
  }, []);

  const generateDilemmas = async (url) => {
    setStep("generating");
    setGenError("");
    try {
      const { ok, data, message } = await api.post("/organization/dna-public/generate", { url });
      if (!ok) throw new Error(message || "Failed to generate dilemmas");
      setDilemmas(data.dilemmas);
      setCompanyContext(data.company_context || "");
      setAnswers(data.dilemmas.map((d) => ({ axis: d.axis, axis_label: d.axis_label, choice: "", example: "" })));
      if (data.company_name) setCompanyName(data.company_name);
      if (data.company_industry) setIndustry(data.company_industry);
      if (data.company_location) setLocation(data.company_location);
      if (data.company_logo) setLogoPreview(data.company_logo);
      setStep("dilemmas");
    } catch (err) {
      console.error(err);
      setGenError(err.message || "Could not analyze this website");
      setStep("enter-website");
    }
  };

  const handleManualGenerate = (e) => {
    e.preventDefault();
    if (!website.trim()) return;
    const url = website.trim().startsWith("http") ? website.trim() : `https://${website.trim()}`;
    setWebsite(url);
    generateDilemmas(url);
  };

  const updateAnswer = (field, value) => {
    const updated = [...answers];
    updated[currentDilemma] = { ...updated[currentDilemma], [field]: value };
    setAnswers(updated);
  };

  const handleProcess = async () => {
    setStep("processing");
    try {
      const { ok, data, message } = await api.post("/organization/dna-public/process", {
        dilemmas: answers,
        generated_dilemmas: dilemmas,
        company_name: companyContext,
      });
      if (!ok) throw new Error(message || "Failed to process");
      setProfile(data);
      setStep("signup");
    } catch (err) {
      console.error(err);
      setStep("dilemmas");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    try {
      const signupRes = await api.post("/user/signup_recruiter", {
        email,
        password,
        firstname: firstName,
        lastname: lastName,
        organization_name: companyName,
      });
      if (!signupRes.ok) throw new Error(signupRes.message || signupRes.code || "Signup failed");

      const { token, organisations } = signupRes;
      const organization = organisations?.[0];
      api.setToken(token);

      const { setUser, setOrganization } = useStore.getState();
      setUser(signupRes.user);
      setOrganization(organization);

      const orgUpdate = { organization_id: organization?._id };
      if (industry) orgUpdate.industry = industry;
      if (location) orgUpdate.location = location;
      if (website) orgUpdate.website = website;
      if (slug) orgUpdate.career_website_slug = slug;
      if (dilemmas.length > 0) orgUpdate.culture_generated_dilemmas = dilemmas;
      if (answers.some((a) => a.choice)) orgUpdate.culture_dilemmas = answers.filter((a) => a.choice);

      if (Object.keys(orgUpdate).length > 1) {
        await api.put("/organization", orgUpdate);
      }

      if (profile) {
        await api.put("/organization", {
          organization_id: organization?._id,
          culture_profile: {
            status: "done",
            processed_at: new Date(),
            axes: profile.axes,
            matching_vector: profile.matching_vector,
          },
          career_text: profile.career_text,
        });
      }

      if (logoFile) {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const fileRes = await api.post("/file", {
              file: { rawBody: reader.result, name: logoFile.name },
              folder: `/organization/${organization?._id}`,
            });
            if (fileRes.ok) await api.put("/organization", { organization_id: organization?._id, logo: fileRes.data });
          } catch (err) {
            console.error("Logo upload failed:", err);
          }
          window.location.href = "/recruiter";
        };
        reader.readAsDataURL(logoFile);
      } else {
        window.location.href = "/recruiter";
      }
    } catch (err) {
      console.error(err);
      setSubmitError(err.message || "Something went wrong");
      setSubmitting(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-[#0b1121] text-white">
      {step === "enter-website" && (
        <div className="flex items-center justify-center min-h-screen px-6">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <div className="text-emerald-400 text-3xl mb-4">✦</div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Discover your hiring DNA</h1>
              <p className="text-gray-400 text-sm">Enter your website and we'll generate personalized culture dilemmas</p>
            </div>
            <form onSubmit={handleManualGenerate}>
              <div className="bg-[#151d30] border border-gray-700/50 rounded-xl p-5 mb-4">
                <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-3">Your company website</label>
                <div className="flex gap-3">
                  <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="yourcompany.com"
                    className="flex-1 bg-[#1a2235] border border-gray-600/50 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
                  <button type="submit" disabled={!website.trim()}
                    className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-6 py-3 rounded-lg transition-all whitespace-nowrap">
                    Go →
                  </button>
                </div>
              </div>
              {genError && <p className="text-red-400 text-sm text-center">{genError}</p>}
            </form>
          </div>
        </div>
      )}

      {step === "generating" && (
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <Spinner size="lg" />
          <h2 className="text-xl font-semibold text-white mt-6 mb-2">Analyzing your website</h2>
          <p className="text-gray-400 text-sm text-center max-w-sm">
            We're scraping <span className="text-emerald-400">{website}</span> and generating 6 personalized culture dilemmas...
          </p>
          <p className="text-gray-600 text-xs mt-4">This usually takes 15–30 seconds</p>
        </div>
      )}

      {step === "processing" && (
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <Spinner size="lg" />
          <h2 className="text-xl font-semibold text-white mt-6 mb-2">Analyzing your culture</h2>
          <p className="text-gray-400 text-sm">Building your hiring DNA profile...</p>
        </div>
      )}

      {step === "dilemmas" && (
        <Dilemmas dilemmas={dilemmas} answers={answers} current={currentDilemma} setCurrent={setCurrentDilemma} onUpdate={updateAnswer} onProcess={handleProcess} />
      )}

      {step === "signup" && (
        <Signup
          companyName={companyName} setCompanyName={setCompanyName}
          industry={industry} setIndustry={setIndustry}
          location={location} setLocation={setLocation}
          logoPreview={logoPreview} onLogoChange={handleLogoChange}
          firstName={firstName} setFirstName={setFirstName}
          lastName={lastName} setLastName={setLastName}
          email={email} setEmail={setEmail}
          password={password} setPassword={setPassword}
          slug={slug} submitting={submitting} error={submitError}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
